import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff } from 'lucide-react';

const CV_ISLANDS = [
  'Santiago', 'São Vicente', 'Santo Antão', 'Fogo', 'Sal',
  'Boa Vista', 'Brava', 'Maio', 'São Nicolau',
];

export default function CandidateRegister() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { registerCandidate } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    country: 'Cabo Verde',
    location: '',
    language_preference: i18n.language
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isCapeVerde = formData.country === 'Cabo Verde';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validação condicional: País e Ilha obrigatórios para Cabo Verde
    if (isCapeVerde && !formData.location) {
      setError('Por favor seleciona a tua ilha.');
      return;
    }

    setLoading(true);

    // Enviar country no payload (campo extra — backend ignora se não reconhecer)
    const payload = { ...formData };

    const result = await registerCandidate(payload);
    
    if (result.verificationRequired) {
      navigate('/verify-email', { state: { email: result.email, userType: result.userType } });
      return;
    }
    if (result.success) {
      navigate('/candidate/profile');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="mb-6">
            <h1 className="text-5xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
                Trab
              </span>
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                adju
              </span>
            </h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">Cabo Verde</p>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('candidate.registerTitle')}</h2>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="candidate-register-form">
          <div>
            <Label htmlFor="full_name">{t('candidate.fullName')}</Label>
            <Input
              id="full_name"
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              data-testid="candidate-name-input"
            />
          </div>

          <div>
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="candidate-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="candidate-password-input"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="phone">{t('auth.phone')}</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+238 ..."
              data-testid="candidate-phone-input"
            />
          </div>

          <div>
            <Label htmlFor="country">
              País <span className="text-red-500">*</span>
            </Label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value, location: '' })}
              required
              data-testid="candidate-country-input"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="Cabo Verde">Cabo Verde</option>
              <option value="Portugal">Portugal</option>
              <option value="Angola">Angola</option>
              <option value="Brasil">Brasil</option>
              <option value="Outro">Outro</option>
            </select>
          </div>

          {isCapeVerde ? (
            <div>
              <Label htmlFor="location">
                Ilha <span className="text-red-500">*</span>
              </Label>
              <select
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
                data-testid="candidate-location-input"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Seleciona a tua ilha…</option>
                {CV_ISLANDS.map(isl => (
                  <option key={isl} value={isl}>{isl}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <Label htmlFor="location">{t('auth.location')}</Label>
              <Input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Cidade, País"
                data-testid="candidate-location-input"
              />
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
            disabled={loading}
            data-testid="candidate-submit-btn"
          >
            {loading ? t('common.loading') : t('candidate.createProfile')}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">{t('auth.hasAccount')} </span>
          <Link to="/candidate/login" className="text-blue-600 hover:text-blue-700 font-medium">
            {t('auth.loginButton')}
          </Link>
        </div>

        <div className="mt-4 text-center">
          <Link to="/start" className="text-sm text-gray-500 hover:text-gray-700">
            ← {t('auth.backLink')}
          </Link>
        </div>
      </Card>
    </div>
  );
}
